import { Component, Inject, OnInit, DestroyRef, inject, computed } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DataService } from './services/data.service';
import { TranslateService } from '@ngx-translate/core';
import { RescanService } from './services/rescan.service';
import { SplashScreenService } from './services/splash-screen.service';
import { StorageService } from './services/storage.service';
import { ThemeService } from './services/theme.service';
import { ConstantsService } from './services/constants.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService } from './services/api.service';
import { CartService } from './services/cart.service';
import { TableSocketService } from './services/table-socket.service';
import { ToastService } from './services/toast.service';
import { MenuClosedResponse } from './models/menu.model';
import { Zone, Config } from './models/config.model';
import { OrderStatus, PaymentMode, VenueType, StoredItem, RTL_LANGUAGES } from './shared/enums';
import { retry } from 'rxjs';
import { MenuService } from './menu/menu.service';
import { FiltersService } from './services/filters.service';
import { SettingService } from './services/setting.service';
import { DirectionService } from './services/direction.service';
import { stripQueryParam } from './shared/query-string.utils';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  isRtl: boolean = false;
  lang: string = 'en';
  dataReady = false;
  splashReady = computed(() => {
    const status = this.splashScreenService.splashState().status;
    return status === 'idle' || status === 'disabled';
  });
  transactionLoaded = computed(() => this.dataService.transactionLoaded());

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private apiService: ApiService,
    private dataService: DataService,
    private translateService: TranslateService,
    private rescanService: RescanService,
    private splashScreenService: SplashScreenService,
    private storageService: StorageService,
    private menuService: MenuService,
    private filtersService: FiltersService,
    private settingService: SettingService,
    private tableSocketService: TableSocketService,
    private cartService: CartService,
    private toastService: ToastService,
    public themeService: ThemeService,
    private constants: ConstantsService,
    private directionService: DirectionService,
  ) { }

  ngOnInit(): void {
    const lang = this.storageService.get(StoredItem.Language) || 'en';
    this.translateService.setDefaultLang(lang);
    this.translateService.use(lang);

    this.dataService.setUserId();
    this.loadData();
  }

  private loadData(): void {
    // Note: This is like anti-angular but whatever, no time to fix bug that should not exist
    let tableId: string | null = this.getFromQueryString(window.location.search, 'table_id');

    if (tableId != null) {
      this.dataService.setTableId(tableId);
      // A menu-list URL carrying table_id can only come from outside the app (in-app
      // navigation drops query params, and a refresh won't have it once stripped).
      if (window.location.pathname.includes('/menu-list/')) {
        this.dataService.markExternalLinkEntry();
      }

      // Gateway returns also carry table_id - only a real QR scan restarts the rescan timer
      const isGatewayReturn = window.location.search.includes('transaction_id')
        || window.location.pathname.startsWith('/bill-paid');
      if (!isGatewayReturn) {
        this.rescanService.markScan();
      }
    } else {
      tableId = this.dataService.getStoredTableId();
      if (window.location.hostname === 'localhost' && tableId === null) {
        tableId = this.constants.LOCAL_DEV_TABLE_ID;
        this.dataService.setTableId(tableId);
      }
      if (tableId === null) {
        console.error('Could not find table, no param table_id given');
        this.showRescanScreen();
        return;
      }
    }

    const ezetapMachineId = this.getFromQueryString(window.location.search, 'ezetap_device_id');

    if (ezetapMachineId) {
      this.dataService.ezetapMachineId.set(ezetapMachineId);
    }

    // Payment-return URLs keep their query intact - bill-paid reads the whole string.
    if (!this.isPaymentRedirectReturn()) {
      stripQueryParam('table_id');
      stripQueryParam('ezetap_device_id');
    }

    this.fetchConfigAndData(tableId);

  }

  private fetchConfigAndData(tableId: string): void {
    this.apiService
      .getConfig(tableId)
      .subscribe({
        next: response => {
          this.setConfig(response);
          this.setTableAndRestaurant(tableId);
        },
        error: (error) => {
          if (error.status === 403) {
            const menuClosed: MenuClosedResponse = error.error;
            this.splashScreenService.updateSplashStatus('menuClosed', menuClosed.image, menuClosed.languages);
            this.themeService.setTheme(null);
            return;
          }
          this.handleBootFetchError(error);
        },
      });
  }

  private handleBootFetchError(error: { status?: number }): void {
    if (error.status === 0 || (error.status ?? 0) >= 500) {
      this.themeService.setTheme(null);
      this.splashScreenService.updateSplashStatus('connectionError');
      return;
    }
    this.showRescanScreen('COMMON.TABLE_NOT_FOUND');
  }

  private showRescanScreen(messageKey: string = 'COMMON.TABLE_NOT_FOUND'): void {
    this.dataReady = true;
    this.themeService.setTheme(null);
    this.toastService.error(this.translateService.instant(messageKey));
    this.rescanService.redirectToRescan();
  }


  private applyLandingPageConfig(config: Config): void {
    if (config.landing_page.has_landing_page) {
      this.dataService.hasLandingPage = config.landing_page.has_landing_page;
      this.dataService.ctaText = config.landing_page.cta_text || null;
      this.dataService.ctaUrl = config.landing_page.cta_url || null;
      if (config.landing_page.wallpaper_url != null) {
        this.themeService.setLandingWallpaper(config.landing_page.wallpaper_url);
      }
      this.dataService.filteredWaiterOptions.set(config.landing_page.waiter_options);
    }
  }

  private setTableAndRestaurant(tableId: string): void {
    // This is a list of domains for which we will NOT enable the timer
    const domainsWithoutTimer = ["elite", "staytus"];
    const rescanTimeByPaymentMode: Partial<Record<PaymentMode, number>> = {
      [PaymentMode.Postpaid]: 10 * 60 * 1000, // 10 minutes
      [PaymentMode.Prepaid]: 3 * 60 * 60 * 1000, // 3 hours
    };

    this.apiService
      .getTableAndRestaurant(tableId)
      .subscribe({
        next: response => {
          this.dataService.table.set(response.table);
          this.dataService.restaurant.set(response.restaurant);
          this.tableSocketService.connectToTable(response.table.id);
          this.reconcilePendingPayment();
          this.themeService.setTheme(response.restaurant.theme);
          const rescanTimeInMs = rescanTimeByPaymentMode[response.restaurant.payment_mode];
          if (response.restaurant.type === VenueType.Default
            && rescanTimeInMs
            && !domainsWithoutTimer.includes(response.restaurant.theme)
            && !this.constants.RESCAN_TIMER_DISABLED_RESTAURANT_IDS.includes(response.restaurant.id)
          ) {
            this.rescanService.startTimer(rescanTimeInMs);
          }
          this.setVoucher();
          this.setMenu(response.zone);
        },
        error: (error) => this.handleBootFetchError(error),
      });
  }

  // Fresh boot with a leftover marker: check whether it actually got paid.
  private reconcilePendingPayment(): void {
    const pending = this.cartService.getPendingPayment();
    if (!pending) return;
    const orderId = pending.orderIds?.[0];
    if (!orderId) {
      this.cartService.clearPendingPayment();
      return;
    }
    this.apiService.getOrder(orderId).subscribe({
      next: order => {
        if (order?.status === OrderStatus.Paid) {
          this.cartService.clearCart();
          this.toastService.success(this.translateService.instant('PAYMENT.CONFIRMED'));
        }
      },
      error: () => undefined
    });
  }

  private setMenu(zone: Zone): void {
    this.dataService.zone.set(zone);
    this.apiService
      .getMenuCached(zone.id)
      .pipe(retry(1))
      .subscribe(responseMenu => {
        this.menuService.setMenu(responseMenu);
        this.dataReady = true;
      });
  }

  private setConfig(config: Config): void {
    var isPaymentRedirectReturn = this.isPaymentRedirectReturn();
    var isSplashSkipped = window.location.search.includes('skip_splash');
    if (isSplashSkipped) {
      stripQueryParam('skip_splash');
    }
    this.dataService.transactionLoaded.set(!isPaymentRedirectReturn);
    if (isPaymentRedirectReturn) {
      // 'disabled' is a no-op in SplashScreenComponent (its status switch has no
      // 'disabled' case), so the component's default placeholder image would still
      // render while dataReady is false. 'idle' IS handled there (isLoaded = true,
      // nothing renders) — use it so a payment-redirect return shows only the
      // theme-preloader's loader-text, not a splash image.
      this.splashScreenService.updateSplashStatus('idle');
    } else if (isSplashSkipped || config.splash_screen == null) {
      this.splashScreenService.updateSplashStatus('disabled');
    } else {
      this.splashScreenService.updateSplashStatus('splash', config.splash_screen);
    }

    this.setTranslation(config.languages);
    this.dataService.paymentMethods.set(config.payment_methods || []);
    this.settingService.venueLanguages.set(config.languages);
    this.settingService.hasMultiLanguage.set(config.languages.length > 1);
    this.settingService.hasMultiCurrency.set(config.has_multiple_currencies);
    this.filtersService.hasFilters.set(config.has_filters);
    this.dataService.isGridLayout.set(config.grid_layout);

    if (config.promotions?.length > 0) {
      this.dataService.promotions.set(config.promotions);
    }
    this.dataService.hasFees$.next(config.fees?.length > 0);
    this.dataService.isPayNowEnabled = config.is_pay_now_enabled;
    this.dataService.tippingConfig.set(config.tipping);
    this.dataService.menuActions.set(config.menu_actions);
    this.dataService.menuBackgroundUrl.set(config.menu_background || null);
    this.dataService.externalPromos.set(config.external_promotions);

    // landing page config
    this.applyLandingPageConfig(config);
  }

  private isPaymentRedirectReturn(): boolean {
    return window.location.pathname.includes('/bill-paid') && window.location.search.length > 0;
  }

  private setVoucher(): void {
    const voucherName = this.getFromQueryString(window.location.search, 'voucher_code');
    if (!voucherName) {
      return;
    }
    this.dataService.voucherName.set(voucherName);
    if (!this.isPaymentRedirectReturn()) {
      stripQueryParam('voucher_code');
    }
  }

  private getFromQueryString(queryString: string, key: string): string | null {
    // We remove the '?' from the query string
    const query: string = queryString.substr(1);

    // We get the individual parameters
    const splitted: string[] = query.split('&');

    // Searching for the index where the key is present
    const index = splitted.findIndex(elem => elem.startsWith(key));

    // index is -1, nothing found
    if (index === -1) {
      return null;
    }

    // We retrieve the couple "key=value"
    const couple = splitted[index];

    // And then we return the value (key [0] and value [1])
    return couple.split('=')[1];
  }

  private setTranslation(languages: string[]): void {
    this.translateService.addLangs(languages);
    this.lang = languages[0] || 'en';

    const storedLang = this.storageService.get(StoredItem.Language);
    if (!storedLang || !languages.find(l => l === storedLang)) {
      this.storageService.remove(StoredItem.Language);
    } else if (languages.find(l => l === storedLang)) {
      this.lang = storedLang;
    }

    this.translateService.use(this.lang);
    this.isRtl = this.isRtlLanguage();
    this.handleRtl();
    this.document.documentElement.lang = this.translateService.currentLang;
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((langEvent) => {
        this.lang = langEvent.lang;
        this.isRtl = this.isRtlLanguage();
        this.handleRtl();
        // Re-fetch config but only apply landing page translation
        this.apiService.getConfig(this.dataService.table().id).subscribe(config => {
          this.applyLandingPageConfig(config);
          this.dataService.tippingConfig.set(config.tipping);
          this.dataService.paymentMethods.set(config.payment_methods || []);
        });
        this.setTableAndRestaurant(this.dataService.table().id);
        this.document.documentElement.lang = this.lang;
      }
      );
  }

  private isRtlLanguage(): boolean {
    return RTL_LANGUAGES.includes(this.lang);
  }

  private handleRtl(): void {
    this.document.documentElement.dir = this.isRtl ? 'rtl' : 'ltr';
    this.directionService.set(this.isRtl ? 'rtl' : 'ltr');
  }
}
